import { useScrollPosition } from "@n8tb1t/use-scroll-position";
import { Portal } from "components/Portal";
import { Vertical } from "gls/lib";
import React, { MutableRefObject, ReactNode, useRef, useState } from "react";
import { ScrollButton } from "components/ScrollContainer/ScrollButton";
import LoadingScreen from "components/Loading/LoadingScreen";
import { TableStyle, wrapperStyle } from "./components/tableStyle";
import { TableHeaderCell } from "./components/TableHeaderCell";
import { TableRow } from "./components/TableRow";
import { EmptyListMessage } from "./components/EmptyListMessage";
import { LoadMask } from "components/LoadMask";


export type Column<T> = {
  title: string | ReactNode
  field: string
  sortField?: string
  noSort?: boolean
  onClick?: () => void
  width?: string
  className?: string
  disabled?: boolean
  hidden?: boolean
  defaultValue?: string
  renderer?: (cellValue: T[keyof T], rowValues: T) => ReactNode | string
  ariaLabel?: string
}

type TableProps<T> = {
  data: T[];
  columns: Column<T>[];
  id?: string;
  renderLoadMore?: React.ReactNode;
  height?: string;
  loading?: boolean;
  showTopButton?: boolean;
  topButtonClick?: () => void;
  onSort?: (field: string) => void;
  onRowClick?: (rowValues: T) => void
  emptyListMsg?: string
};

export const DataTable = <T extends Record<string, any>>(props: TableProps<T>) => {
  const [showOnScroll, setShowOnScroll] = useState(false);
  const boundingElementRef = useRef<HTMLDivElement>(null);
  const elementRef = useRef<HTMLTableElement>(null);
  const {
    showTopButton = true,
    loading,
    data,
    id = 'data-table',
    emptyListMsg = "No items to display"
  } = props;

  useScrollPosition(({ prevPos, currPos }) => {
    const isShow = currPos.y > 10;
    if (isShow !== showOnScroll) setShowOnScroll(isShow);
  },
    [showOnScroll],
    elementRef as MutableRefObject<HTMLTableElement>,
    false,
    300,
    boundingElementRef as MutableRefObject<HTMLDivElement>
  );

  const handleScroll = () => {
    props.topButtonClick && props.topButtonClick()
    boundingElementRef.current?.scrollTo({
      behavior: "smooth",
      top: 0,
    });
  };

  if (data.length === 0) {
    if (loading)
      return (
        <Vertical className={wrapperStyle} sizing="stretch">
          <LoadingScreen />
        </Vertical>
      )
    else
      return (
        <Vertical className={wrapperStyle} sizing="stretch">
          <EmptyListMessage title={emptyListMsg} />
        </Vertical>
      )
  }

  return (
    <Vertical className={wrapperStyle} sizing="stretch">
      <div className={TableStyle.container} ref={boundingElementRef} tabIndex={-1}>
        <LoadMask isShow={loading === true} />
        <table
          cellPadding="0"
          className={TableStyle.table}
          ref={elementRef}
          id={id}
        >
          <thead>
            <tr>
              {props.columns.filter(c => !c.hidden).map((column, index) =>
                <TableHeaderCell<T>
                  key={`column-${index}`} column={column} onSort={props.onSort} />
              )}
            </tr>
          </thead>
          <tbody className={TableStyle.tBody}>
            {props.data.map((row, index) =>
              <TableRow<T>
                key={`row-${index}`}
                columns={props.columns}
                rowValues={row}
                rowIndex={index}
                onRowClick={props.onRowClick}
              />
            )}
          </tbody>
        </table>
        {props.renderLoadMore && props.renderLoadMore}
      </div>
      <Portal>
        {showTopButton && showOnScroll && <ScrollButton onClick={handleScroll} />}
      </Portal>
    </Vertical>
  );
};


